import { ClockCounterClockwise, Plus, X } from '@phosphor-icons/react';

import { relativeFr } from '../lib/humanize';
import { cn } from '@/lib/utils';

export interface HistoryThread {
  id: string;
  title: string;
  last_message_at: string;
}

/* Historique des conversations d'un enfant, affiché dans la page de chat
   parent/tuteur pour changer de fil sans quitter la discussion. */
export function StudentChatHistory({
  threads,
  activeThreadId,
  onSelect,
  onNew,
  onClose,
}: {
  threads: HistoryThread[];
  activeThreadId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onClose: () => void;
}) {
  return (
    <div className="flex flex-col h-full min-h-0 bg-background">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border shrink-0">
        <ClockCounterClockwise size={16} weight="bold" aria-hidden className="shrink-0 text-muted-foreground" />
        <span className="flex-1 font-extrabold text-[13px] text-foreground">Historique</span>
        <button
          type="button"
          onClick={onNew}
          aria-label="Nouvelle conversation"
          title="Nouvelle conversation"
          className="min-w-11 min-h-11 grid place-items-center rounded-[var(--naia-r-sm)] border border-border bg-background text-foreground cursor-pointer hover:bg-accent hover:text-primary transition-colors"
        >
          <Plus size={15} weight="bold" />
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer l'historique"
          title="Fermer"
          className="min-w-11 min-h-11 grid place-items-center rounded-[var(--naia-r-sm)] border border-border bg-background text-foreground cursor-pointer hover:bg-accent hover:text-primary transition-colors"
        >
          <X size={15} weight="bold" />
        </button>
      </div>

      <nav aria-label="Historique des conversations" className="flex-1 min-h-0 overflow-y-auto p-3 flex flex-col gap-2">
        {threads.length === 0 ? (
          <p className="px-3 py-4 text-[13px] text-muted-foreground">Aucune conversation pour l'instant.</p>
        ) : (
          threads.map((t) => {
            const active = t.id === activeThreadId;
            return (
              <button
                key={t.id}
                type="button"
                aria-current={active ? 'true' : undefined}
                onClick={() => onSelect(t.id)}
                className={cn(
                  'flex flex-col items-start gap-1 w-full px-4 py-3 min-h-[64px] shrink-0 rounded-[var(--naia-r-sm)] border border-border cursor-pointer font-[inherit] text-left transition-colors',
                  active ? 'bg-accent text-primary' : 'bg-transparent text-foreground hover:bg-accent hover:text-primary',
                )}
              >
                <span className="w-full text-[13.5px] leading-snug font-semibold overflow-hidden text-ellipsis whitespace-nowrap">
                  {t.title}
                </span>
                <span className="text-[11.5px] leading-snug text-muted-foreground">{relativeFr(t.last_message_at)}</span>
              </button>
            );
          })
        )}
      </nav>
    </div>
  );
}
